import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { KidEmpty } from '../../../../design-system/kid';
import { LearnerRequestView, getLearnerRequests } from '../../api/learner-explore';
import { REQUEST_REASON_WORDS, REQUEST_STATE_WORDS } from '../../learner/requestWords';

type Load =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; requests: LearnerRequestView[] };

/**
 * DEC-S2-S06-05, S06-09 — where the learner's requests stand.
 *
 * The same states the Parent reads, in a child's words. The downstream decision
 * is shown; how it was paid for is not on this payload at all.
 */
const LearnerRequests: React.FC = () => {
  const [load, setLoad] = useState<Load>({ status: 'loading' });

  const fetchRequests = useCallback(() => {
    setLoad({ status: 'loading' });
    getLearnerRequests()
      .then(({ requests }) => setLoad({ status: 'ready', requests }))
      .catch(() => setLoad({ status: 'error' }));
  }, []);

  useEffect(fetchRequests, [fetchRequests]);

  return (
    <>
      <h1 className="hi">Mis solicitudes</h1>
      <p className="hi-s">Las clases que le pediste a tu familia.</p>

      {load.status === 'loading' && (
        <p className="hi-s" role="status">
          Cargando tus solicitudes…
        </p>
      )}

      {load.status === 'error' && (
        <div className="lrn-status" role="alert">
          <h2>No pudimos cargar tus solicitudes</h2>
          <p>No es culpa tuya. Prueba otra vez en un momento.</p>
          <button className="ag-b" onClick={fetchRequests}>
            Intentar otra vez
          </button>
        </div>
      )}

      {load.status === 'ready' && load.requests.length === 0 && (
        <KidEmpty
          icon="💌"
          title="Todavía no has pedido clases"
          detail="Cuando pidas una, aquí verás qué decidió tu familia."
        />
      )}

      {load.status === 'ready' && load.requests.length > 0 && (
        <div className="grid">
          {load.requests.map((request) => (
            <article className="cc" key={request.requestId} aria-label={request.courseTitle}>
              <div className="cc-b">
                <h2 className="cc-t">{request.courseTitle}</h2>
                <span className="lrn-tag wait">{REQUEST_STATE_WORDS[request.state]}</span>
                {request.reason && REQUEST_REASON_WORDS[request.reason] && (
                  <p className="cc-w">{REQUEST_REASON_WORDS[request.reason]}</p>
                )}
                <p>
                  <Link
                    className="lrn-link"
                    to={`/student/explore/${encodeURIComponent(request.courseId)}`}
                  >
                    Ver la clase
                  </Link>
                </p>
              </div>
            </article>
          ))}
        </div>
      )}
    </>
  );
};

export default LearnerRequests;
